import { logger } from './logger';
import api from './api';

export interface UserActivity {
  timestamp: string;
  userId: string;
  activityType: string;
  resourceId?: string;
  details: {
    userEmail?: string;
    [key: string]: unknown;
  };
}

export interface ActivityResponse {
  activities: UserActivity[];
  count: number;
}

/**
 * Enriched activity with resolved ALI number, editor ALI ID, and aggregation info.
 */
export interface EnrichedUserActivity {
  timestamp: string;
  userId: string;
  activityType: string;
  resourceId?: string;
  aliNumber?: string;
  editorAliId?: string;
  aggregatedCount: number;
  details: {
    userEmail?: string;
    section?: string;
    oldVisibility?: string;
    newVisibility?: string;
    [key: string]: unknown;
  };
}

export interface EnrichedActivityResponse {
  activities: EnrichedUserActivity[];
  count: number;
  nextCursor?: string;
}

export const fetchUserActivities = async (
  days: number = 7,
  activityType?: string
): Promise<ActivityResponse> => {
  try {
    const params = new URLSearchParams();
    params.append('days', days.toString());
    if (activityType) {
      params.append('activityType', activityType);
    }

    const response = await api.get(`/api/user/activities?${params}`);
    return response.data;
  } catch (error) {
    logger.error('Error fetching user activities', error);
    throw error;
  }
};

/**
 * Fetch enriched user activities with resolved ALI numbers and aggregation.
 *
 * @param days - Number of days to look back (default 7)
 * @param activityType - Optional activity type filter
 * @param aliCatalogNumber - Optional ALI catalog number for item-scoped mode
 */
export const fetchEnrichedUserActivities = async (
  days: number = 7,
  activityType?: string,
  aliCatalogNumber?: string
): Promise<EnrichedActivityResponse> => {
  try {
    const params = new URLSearchParams();
    params.append('days', days.toString());
    if (activityType) {
      params.append('activityType', activityType);
    }
    if (aliCatalogNumber) {
      params.append('aliCatalogNumber', aliCatalogNumber);
    }

    const response = await api.get(`/api/user/activities/enriched?${params}`);
    return response.data;
  } catch (error) {
    logger.error('Error fetching enriched user activities', error);
    throw error;
  }
};
